import { useCallback, useEffect, useRef, useState } from "react";
import { api, API } from "../../lib/api";

export const guestId = () => { let g = localStorage.getItem("vai_guest"); if (!g) { g = `g${Math.random().toString(36).slice(2, 12)}${Date.now().toString(36)}`; localStorage.setItem("vai_guest", g); } return g; };
const hdr = () => ({ "X-Guest-Id": guestId() });

export function useVelocityChat({ user, autoCreate = true, mode = "general" }) {
  const [models, setModels] = useState([]);
  const [convs, setConvs] = useState([]);
  const [conv, setConv] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [model, setModel] = useState(localStorage.getItem("vai_model") || "gpt-5.5");
  const [streaming, setStreaming] = useState(false);
  const [activeTool, setActiveTool] = useState(null);
  const abortRef = useRef(null);
  const h = { headers: hdr() };

  const loadConvs = useCallback(() => api.get(`/ai/conversations${mode === "sales" ? "?mode=sales" : ""}`, h).then((r) => { setConvs(r.data.conversations); return r.data.conversations; }).catch(() => []), []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { api.get("/ai/models").then((r) => setModels(r.data.models)).catch(() => {}); }, []);
  useEffect(() => { loadConvs(); }, [user?.user_id, user?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { localStorage.setItem("vai_model", model); }, [model]);

  const open = useCallback(async (c) => { setConv(c); if (!c) { setMsgs([]); return; } const r = await api.get(`/ai/conversations/${c.conv_id}/messages`, h); setMsgs(r.data.messages); if (r.data.conversation.model) setModel(r.data.conversation.model); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const create = useCallback(async () => { const r = await api.post("/ai/conversations", { model, mode }, h); setConvs((x) => [r.data, ...x]); setConv(r.data); setMsgs([]); return r.data; }, [model]); // eslint-disable-line react-hooks/exhaustive-deps
  const remove = useCallback(async (c) => { await api.delete(`/ai/conversations/${c.conv_id}`, h); setConvs((x) => x.filter((y) => y.conv_id !== c.conv_id)); if (conv?.conv_id === c.conv_id) { setConv(null); setMsgs([]); } }, [conv]); // eslint-disable-line react-hooks/exhaustive-deps
  const rename = useCallback(async (c, title) => { await api.patch(`/ai/conversations/${c.conv_id}`, { title, model: c.model }, h); loadConvs(); }, [loadConvs]); // eslint-disable-line react-hooks/exhaustive-deps

  const send = useCallback(async (text, attachments = []) => {
    if (streaming) return;
    let c = conv;
    if (!c) { if (!autoCreate) return; c = await create(); }
    const um = { msg_id: `tmp_${Date.now()}`, role: "user", content: text, attachments, created_at: new Date().toISOString() };
    const am = { msg_id: `tmp_a_${Date.now()}`, role: "assistant", content: "", tools: [], images: [], model, pending: true };
    setMsgs((x) => [...x, um, am]);
    setStreaming(true); setActiveTool(null);
    const ctrl = new AbortController(); abortRef.current = ctrl;
    try {
      const res = await fetch(`${API}/ai/conversations/${c.conv_id}/messages`, { method: "POST", credentials: "include", signal: ctrl.signal, headers: { "Content-Type": "application/json", ...hdr() }, body: JSON.stringify({ text, model, attachment_ids: attachments.map((a) => a.file_id) }) });
      if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.detail || `HTTP ${res.status}`); }
      const reader = res.body.getReader(); const dec = new TextDecoder(); let buf = "";
      const patch = (fn) => setMsgs((x) => x.map((m) => (m.msg_id === am.msg_id ? fn(m) : m)));
      for (;;) {
        const { value, done } = await reader.read(); if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n"); buf = parts.pop();
        for (const p of parts) {
          if (!p.startsWith("data: ")) continue;
          let ev; try { ev = JSON.parse(p.slice(6)); } catch { continue; }
          if (ev.t) patch((m) => ({ ...m, content: m.content + ev.t, error: ev.error || m.error }));
          if (ev.tool) { setActiveTool(ev.status === "start" ? ev.tool : null); if (ev.status === "done") patch((m) => ({ ...m, tools: [...(m.tools || []), { name: ev.tool, ok: ev.ok }] })); }
          if (ev.done) patch((m) => ({ ...m, msg_id: ev.msg_id, pending: false, tools: ev.tools, images: ev.images }));
        }
      }
    } catch (e) {
      if (e.name !== "AbortError") setMsgs((x) => x.map((m) => (m.msg_id === am.msg_id ? { ...m, content: m.content || `Something went wrong: ${e.message}`, error: true, pending: false } : m)));
    } finally { setStreaming(false); setActiveTool(null); abortRef.current = null; loadConvs(); }
  }, [conv, model, streaming, create, autoCreate, loadConvs]);

  const stop = () => abortRef.current?.abort();
  return { models, convs, conv, msgs, model, setModel, streaming, activeTool, open, create, remove, rename, send, stop, guest: !user };
}

export async function uploadAttachment(file) {
  const fd = new FormData(); fd.append("file", file);
  const r = await api.post("/ai/upload", fd, { headers: { "Content-Type": "multipart/form-data", ...hdr() } });
  return r.data;
}

export async function transcribe(blob) {
  const fd = new FormData(); fd.append("file", blob, "clip.webm");
  const r = await api.post("/ai/transcribe", fd, { headers: { "Content-Type": "multipart/form-data", ...hdr() } });
  return r.data.text;
}

export async function speak(text, voice = "nova") {
  const r = await api.post("/ai/speak", { text, voice }, { headers: hdr(), responseType: "blob" });
  return URL.createObjectURL(r.data);
}
